import { Button, ErrorState, LoadingState } from '@buakei/ui';
import { useMutation } from '@tanstack/react-query';
import { NotebookPen } from 'lucide-react';
import type { IdentityClient } from '@/auth/identity-client';
import { assistantApi } from '@/services/api';
import { AnswerView } from './ActsAssistant';

/**
 * Case brief of one judgment (ADR-020): facts, issues, holding, reasoning, ratio and later
 * treatment, each point cited to the judgment's pages. A brief already written is shown at
 * once; writing a new one uses one of the day's questions.
 */
export function CaseBriefPanel({
  tokens,
  versionId,
}: {
  tokens: IdentityClient;
  versionId: string;
}) {
  const brief = useMutation({ mutationFn: () => assistantApi.brief(tokens, versionId) });

  if (brief.isPending) {
    return <LoadingState label="Reading the judgment and writing the brief" />;
  }
  if (brief.isError) {
    return (
      <ErrorState title="The brief could not be prepared" onRetry={() => brief.mutate()}>
        {brief.error.message}
      </ErrorState>
    );
  }
  if (!brief.data) {
    return (
      <div className="flex flex-col items-start gap-2">
        <Button onClick={() => brief.mutate()}>
          <NotebookPen aria-hidden /> Show the case brief
        </Button>
        <p className="text-sm text-muted-600">
          Facts, issues, holding, reasoning and ratio, each cited to the judgment's pages. If no
          brief has been written for this case yet, writing it uses one of today's questions.
        </p>
      </div>
    );
  }
  if (brief.data.outcome !== 'answered') {
    return (
      <p role="status" className="text-ink-900">
        A brief could not be written from this judgment's text. Open the judgment below to read it.
      </p>
    );
  }
  return (
    <section aria-label="Case brief" className="flex flex-col gap-3">
      <h2 className="font-serif text-xl font-semibold text-navy-900">Case brief</h2>
      <AnswerView answer={brief.data} turnId={0} />
    </section>
  );
}
